import { FC, useMemo } from "react";
import { useTranslations } from "@jugl-web/utils";
import { Button } from "@jugl-web/ui-kit";
import { Plan } from "@jugl-web/rest-api";
import { SubscriptionIntervalType } from "@jugl-web/rest-api/entities/models/common-types/SubscriptionIntervalType";
import { getPlanActionState } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { ReactComponent as ArrowRightWhiteIcon } from "../../assets/arrow-right-white.svg";
import { ReactComponent as ArrowRightDarkIcon } from "../../assets/arrow-right-dark.svg";

interface PlanActionButtonProps {
  plan: Plan;
  allPlans: Plan[];
  selectedInterval: SubscriptionIntervalType;
  size: "sm" | "lg";
  isMostPopular?: boolean;
  className?: string;
  fullWidth?: boolean;
  onClick?: () => void;
  /** override for external reasons (e.g. downgrade-blocked by seat count) */
  isDisabled?: boolean;
}

export const PlanActionButton: FC<PlanActionButtonProps> = ({
  plan,
  allPlans,
  selectedInterval,
  size,
  isMostPopular,
  className,
  fullWidth,
  onClick,
  isDisabled: isDisabledOverride,
}) => {
  const { t } = useTranslations();
  const { currentPlan, isMonthly, isSubscriptionPendingCancel } =
    useSubscriptionV2();

  const currentInterval: SubscriptionIntervalType | undefined = currentPlan
    ? isMonthly
      ? "month"
      : "year"
    : undefined;

  const actionState = useMemo(
    () =>
      getPlanActionState({
        plan,
        allPlans,
        currentPlanId: currentPlan?.id,
        currentInterval,
        selectedInterval,
        isSubscriptionPendingCancel,
        isMostPopular,
      }),
    [
      plan,
      allPlans,
      currentPlan?.id,
      currentInterval,
      selectedInterval,
      isSubscriptionPendingCancel,
      isMostPopular,
    ]
  );

  const isDisabled = isDisabledOverride ?? actionState.isDisabled;
  const label = t({
    id: `subscription-plans-dialog.${actionState.labelId}`,
    defaultMessage: actionState.defaultLabel,
  });

  const showArrow = !isDisabled && !actionState.isCurrentPlan;
  const iconEnd = showArrow ? (
    actionState.variant === "primary" ? (
      <ArrowRightWhiteIcon className="transition-all" />
    ) : (
      <ArrowRightDarkIcon className="transition-all" />
    )
  ) : undefined;

  return (
    <Button
      variant={actionState.variant}
      size={size}
      fullWidth={fullWidth}
      iconEnd={iconEnd}
      isDisabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      className={className}
    >
      {label}
    </Button>
  );
};
